import React, {useContext, useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {Alert, Button, Icon, Input, Spacer, SubTitle, Text, Theme, Toggle} from "@dspworkplace/ui";
import {getCompanyId, useAuth} from "../../../../Auth";
import SettingsContext from "../../context";
import {toast} from "../../../../components/Toasts";
import Tooltip from "../../../../components/Tooltip";
import {Link} from "../../../../components/UI";

function CopyToClipboardButton({
    text,
    ...props
}) {
    const [copied, setCopied] = useState(false);

    async function copyToClipboard(textToCopy) {
        // Navigator clipboard api needs a secure context (https)
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } else {
            // Use the 'out of viewport hidden text area' trick
            const textArea = document.createElement("textarea");
            textArea.value = textToCopy;

            // Move textarea out of the viewport so it's not visible
            textArea.style.position = "absolute";
            textArea.style.left = "-999999px";

            document.body.prepend(textArea);
            textArea.select();

            try {
                document.execCommand('copy');
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (error) {

            } finally {
                textArea.remove();
            }
        }
    }

    return (
        <Tooltip content={!copied ? 'Copy to clipboard' : 'Copied!'}>
            <Button
                // size='small'
                Type='button'
                type={"default"}
                {...props}
                onClick={()=> copyToClipboard(text)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    top: 26,
                    marginLeft: 8,
                    backgroundColor: copied ? Theme.colors.success.bg : null,
                    ...props?.style
                }}
            >
                {!copied ? (
                    <Icon.Copy size={'20px'}/>
                ) : (
                    <Icon.Check size={'20px'} color={Theme.colors.success.text}/>
                )}
            </Button>
        </Tooltip>
    );
}

export default function ADPSettings() {
    const { api } = useAuth();
    const [state, dispatch] = useContext(SettingsContext);
    const { register, handleSubmit, reset, watch, errors, getValues } = useForm();

    useEffect(() => reset(state.company), [state.company]);
    const isAdpEnable = watch("isAdp");

    function randomStringGenerator(len) {
        let length = (len) ? (len) : (20);
        let string = "abcdefghijklmnopqrstuvwxyz0123456789";
        let password = "";
        while (password.length < length) {
            let entity1 = Math.ceil(string.length * Math.random() * Math.random());
            let hold = string.charAt(entity1);
            password += hold;
        }

        return password.substring(0, len).toUpperCase();
    }

    useEffect(() => {
        if (state?.company && state?.company?.id && (state.company.connectorCode === null || state.company.connectorCode === '')) {
            let generatedCode = randomStringGenerator(36);
            let param = {
                actions: {
                    update: {
                        Company: {
                            update_company: {
                                findBy: {
                                    id: getCompanyId(),
                                },
                                updateRecord: {
                                    connectorCode: generatedCode,
                                },
                            },
                        },
                    },
                },
            };
            api.post("/api/lazy/manage/data", param).then((response) => {
                let stateCompany = state.company;
                stateCompany.connectorCode = generatedCode;
                reset(stateCompany);
                dispatch({
                    type: "UPDATE_COMPANY",
                    payload: {
                        company: stateCompany
                    },
                });
            });
        }
    },[state.company])

    const handleADPSetting = (data, evt) => {
        let form = evt.target;
        form.querySelector("button").disabled = true;
        let param = {
            actions: {
                update: {
                    Company: {
                        update_company: {
                            findBy: {
                                id: getCompanyId(),
                            },
                            updateRecord: {
                                isAdp: data.isAdp,
                                connectorCode: data.connectorCode,
                                uniqueAccountIdentifier: data.uniqueAccountIdentifier,
                            },
                        },
                    },
                },
            },
        };
        api.post("/api/lazy/manage/data", param).then((response) => {
            localStorage.setItem("isADP", data.isAdp);
            dispatch({
                type: "UPDATE_ADP",
                payload: {
                    isAdp: data.isAdp,
                },
            });
            if (data.isAdp) {
                let paramsPaycomSettings = {
                    actions: {
                        response: {
                            PaycomSettings: {
                                custom: {
                                    functionName: "updatePaycomSettings",
                                    criteria: {
                                        company: getCompanyId(),
                                        isEnabled: 0,
                                    },
                                    get: "PaycomSettings",
                                },
                            },
                        },
                    },
                };
                api.post("/api/lazy/manage/data", paramsPaycomSettings);
            }
            form.querySelector("button").disabled = false;
            toast({
                type: "success",
                title: "ADP Setting",
                content: "ADP Settings updated.",
            });
        });
    };

    return (
        <>
            <form onSubmit={handleSubmit(handleADPSetting)}>
                <SubTitle>ADP Marketplace Connector Settings</SubTitle>
                <Spacer bottom={3} />
                <Text style={{maxWidth: 800}}>
                    Integrate your ADP employees, punch times and time offs using our new
                    {' '}<Link href={'https://apps.adp.com/'} target='_blank'>ADP Marketplace Connector APP</Link>.
                    <br/>
                    <br/>
                    Once you have added the dspworkplace marketplace app to your ADP account:
                    <br/>
                    • <Link href={'https://apps.adp.com/account/apps'} target='_blank'>Manage your ADP billing in here</Link>
                    <br/>
                    • <Link href={'https://apps.adp.com/account/users'} target='_blank'>Manage ADP user management within the app here</Link>
                </Text>
                <Spacer bottom={5} />
                <div style={{display: "inline-flex"}}>
                    <Alert.Warning
                        useIcon
                        title={'Client Data Access Authorization'}
                        content={<>
                            ADP requires that the DSP owner grants the dspworkplace Marketplace Connector APP consent to access the employee data.
                            <br/>
                            Use <Link
                                style={{fontSize: 'inherit'}}
                                target='_blank'
                                href='https://adpapps.adp.com/consent-manager/pending/direct?consumerApplicationID=89bb7ecf-fc65-4b35-8c42-36ac2cee9cc2&successUri=https://apps.adp.com/myapps'
                            >this link</Link> to provide consent if not done yet.
                        </>}
                    />
                </div>
                <Spacer bottom={5} />
                <Toggle
                    ref={register}
                    name="isAdp"
                    // help="If this is enabled, the system will automatically fetch ADP punch data."
                    singleText="Enable ADP Integration"
                    on={isAdpEnable}
                />
                <Spacer bottom={5} />
                <Input
                    ref={register}
                    name="connectorCode"
                    label="ADP Connector Code"
                    readOnly
                    onClick={e => e.target.select()}
                />
                <CopyToClipboardButton text={watch('connectorCode')}/>
                <Spacer bottom={3} />
                <Input
                    ref={register}
                    name="uniqueAccountIdentifier"
                    label="Unique Account Identifier"
                    readOnly
                />
            </form>
        </>
    );
}
